/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {StateService} from '@uirouter/core';
import {finalize} from 'rxjs';
import * as _ from 'lodash';

import template from './ad-unit-list-page.component.html';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {getCountryName} from '../common/directives/country-select/countries';
import {bulkApiErrorsFromResponse} from '../common/controller/bulk-action-errors';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdUnitApiService, AdUnitModel} from './ad-unit-api.service';
import {AdUnitBulkActionService} from './ad-unit-bulk-action.service';
import {AdUnitSiteSearchState} from './ad-unit-search-state';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-list-page',
  template
})
export class AdUnitListPageComponent implements OnInit {
  constructor(private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()) {
  }

  readonly itemStateName = 'app.inv.ad-units.ad-unit';
  readonly siteItemStateName = 'app.inv.sites.site';
  readonly showCountry = this.localNetworkInfo.getSiteCountries().length > 1;
  readonly showFullPage = inject(LocalNetworkProfile).isPureMarketplacePlatform()
    && ['adnuntius@adnuntius.com', 'broker1@adnuntius.com'].includes(inject(LocalUserProfile).get('username'));
  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'dimensions', sortable: false},
    {label: 'site', param: 'Site'},
    ...(this.showCountry ? [{label: 'country', param: '', sortable: false}] : []),
    {label: 'status', param: 'requestedInLastDay'},
    {label: 'tagId', param: 'tagId'},
    ...(this.showFullPage ? [{label: 'isFullPage', param: 'fullPage'}] : [])
  ];
  readonly list = inject(SignalAdminResourceListStateFactory).create<AdUnitModel>(
    inject(AdUnitApiService),
    {listId: 'fullAdUnitList', baseTable: 'adUnit'},
    inject(DestroyRef)
  );
  readonly siteSearchState = new AdUnitSiteSearchState(inject(AdUnitApiService), inject(DestroyRef));
  selectedSite: any = null;

  private readonly modalLoading = inject(AdnModalLoadingService);
  private readonly bulkActions = inject(AdUnitBulkActionService);
  private readonly stateService = inject(StateService);
  private readonly destroyRef = inject(DestroyRef);

  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  ngOnInit(): void {
    this.list.load();
  }

  countryName(adUnit: AdUnitModel): string {
    return getCountryName(adUnit.country);
  }

  copyTagId(tagId: string): void {
    navigator.clipboard?.writeText(tagId);
  }

  onSiteChange(): void {
    this.list.setAdditionalParams(this.selectedSite?.id ? {site: this.selectedSite.id} : {});
  }

  openBulkActions(): void {
    const adapter: any = {
      bulkSelection: this.list.selected(),
      miscBulkAction: (modal: any, modalController: any, items: Partial<AdUnitModel>[]) => {
        if (!items.length) {
          modal.close();
          return;
        }
        this.modalLoading.applyModalLoading();
        this.list.saveMiscBulkAction(items).pipe(
          finalize(() => this.modalLoading.removeModalLoading()),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => {
            modal.close({items});
          },
          error: (response) => {
            modalController.form = modalController.form || {};
            modalController.form.apiErrors = bulkApiErrorsFromResponse(response);
            modalController.form.bulkError = true;
          }
        });
      }
    };
    this.bulkActions.createBulkAction(adapter)();
  }

  openSelectedAsAdTags(): void {
    const tags = _.map(this.list.selected(), (adUnit) => ({
      auId: adUnit.tagId,
      auW: adUnit.width || adUnit.dimensions?.[0]?.[0] || '',
      auH: adUnit.height || adUnit.dimensions?.[0]?.[1] || '',
      auName: adUnit.name
    }));
    this.stateService.go('app.ad-tag', {design: JSON.stringify(tags)});
  }
}
